import React from "react";
import { useTranslation } from "react-i18next";
import { AlertCircle, AlertTriangle, CheckCircle2, Info, X } from "lucide-react";
import { cn } from "@/lib/utils";

export type AlertVariant = "success" | "warning" | "error" | "info";

export interface AlertProps {
	/** Heading (mandatory). i18n key unless `skipTranslation`. */
	title: string;
	/**
	 * Optional body text (i18n key unless `skipTranslation`). For richer
	 * content use `children` instead — `children` takes precedence.
	 */
	description?: string;
	/** Optional rich body content (overrides `description`). */
	children?: React.ReactNode;
	/** Status colour + default icon. Defaults to `info`. */
	variant?: AlertVariant;
	/** Skip i18n translation of `title`/`description`. */
	skipTranslation?: boolean;
	/**
	 * Leading icon: `true` (default) shows the variant's icon, `false` hides
	 * it, or pass a custom node.
	 */
	icon?: boolean | React.ReactNode;
	/** When provided, renders a dismiss (✕) button that calls this. */
	onClose?: () => void;
	/** Optional node rendered at the right (e.g. an action button/link). */
	action?: React.ReactNode;
	/** Extend/override the container classes. */
	className?: string;
}

const variantStyles: Record<
	AlertVariant,
	{ container: string; accent: string; Icon: React.ComponentType<{ className?: string }> }
> = {
	success: {
		container: "border-success/30 bg-success/10",
		accent: "text-success",
		Icon: CheckCircle2,
	},
	warning: {
		container: "border-warning/30 bg-warning/5",
		accent: "text-warning",
		Icon: AlertTriangle,
	},
	error: {
		container: "border-danger/40 bg-danger-muted",
		accent: "text-danger",
		Icon: AlertCircle,
	},
	info: {
		container: "border-action/30 bg-info-muted",
		accent: "text-action",
		Icon: Info,
	},
};

/**
 * An inline, non-toast status message (a.k.a. "alert" / "callout"). Renders a
 * mandatory coloured title with an optional body, a status icon, and optional
 * dismiss/action affordances. Domain-free — pass i18n keys (or literals with
 * `skipTranslation`). For modal confirmations use `AppAlertDialog` instead.
 */
const Alert: React.FC<AlertProps> = ({
	title,
	description,
	children,
	variant = "info",
	skipTranslation = false,
	icon = true,
	onClose,
	action,
	className,
}) => {
	const { t } = useTranslation();
	const { container, accent, Icon } = variantStyles[variant];

	const heading = skipTranslation ? title : t(title as any);
	const body =
		children ?? (description ? (skipTranslation ? description : t(description as any)) : null);

	return (
		<div
			role="alert"
			className={cn(
				"flex items-start gap-3 rounded-lg border border-solid p-4",
				container,
				className
			)}
		>
			{icon !== false &&
				(icon === true ? (
					<Icon className={cn("mt-0.5 h-5 w-5 shrink-0", accent)} />
				) : (
					<span className={cn("mt-0.5 shrink-0", accent)}>{icon}</span>
				))}

			<div className="min-w-0 flex-1">
				<p className={cn("text-sm font-semibold", accent)}>{heading}</p>
				{body && <div className="mt-1 text-sm text-foreground/80">{body}</div>}
			</div>

			{action && <div className="shrink-0">{action}</div>}

			{onClose && (
				<button
					type="button"
					onClick={onClose}
					aria-label="Close"
					className={cn(
						"-mr-1 -mt-1 shrink-0 rounded p-1 text-foreground/40 transition-colors hover:bg-black/5 hover:text-foreground/70"
					)}
				>
					<X className="h-4 w-4" />
				</button>
			)}
		</div>
	);
};

export default Alert;
